import React, { useEffect, useState } from 'react';
import { Button } from 'flowbite-react';
import { useAccountApi } from '../store';
import ApplicationBar from '../components/shared/ApplicationBar';
import Footer from '../components/footers/Footer';
import Loader from '../components/shared/Loader';
import AlertComponent from '../components/shared/AlertComponent';
import BackButton from '../components/shared/BackButton';

interface Transaction {
    id: string;
    type: string;
    status: string;
    amount: number | string;
    commission?: number | string;
    charged_fees?: number | string;
    created_at: string;
    event?: { id: string; name: string } | null;
    user?: { id: string; username: string } | null;
    paypal_order_id?: string;
}

const fmtCurrency = (n: any) => {
    const num = parseFloat(n ?? 0);
    if (isNaN(num)) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

const fmtDate = (s: string) => {
    if (!s) return '—';
    try {
        return new Date(s).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return s;
    }
};

const statusColor = (s: string) => {
    switch (s) {
        case 'active':    return 'bg-green-100 text-green-800';
        case 'completed': return 'bg-blue-100 text-blue-800';
        case 'pending':   return 'bg-yellow-100 text-yellow-800';
        case 'refunded':  return 'bg-orange-100 text-orange-800';
        case 'failed':    return 'bg-red-100 text-red-800';
        default:          return 'bg-gray-100 text-gray-700';
    }
};

const typeColor = (t: string) => {
    switch (t) {
        case 'incoming': return 'bg-emerald-50 text-emerald-700';
        case 'outgoing': return 'bg-red-50 text-red-700';
        default:         return 'bg-gray-50 text-gray-600';
    }
};

const Transactions = () => {
    const { getUserTransactions } = useAccountApi();

    const [currentView, setCurrentView] = useState<'search' | 'loader' | 'results' | 'error'>('search');
    const [userId, setUserId] = useState('');
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [message, setMessage] = useState('');

    useEffect(() => {
        document.title = 'FestiShare Support - Transactions';
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const id = (e.currentTarget.elements.namedItem('userId') as HTMLInputElement).value.trim();
        setUserId(id);
        setCurrentView('loader');
        try {
            const result = await getUserTransactions(id || undefined);
            if (result?.res === 'success') {
                setTransactions(Array.isArray(result.data) ? result.data : []);
                setCurrentView('results');
            } else {
                setMessage(result?.message || 'No transactions found.');
                setCurrentView('error');
            }
        } catch {
            setMessage('An unexpected error occurred.');
            setCurrentView('error');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="mt-6 mb-24 mx-auto max-w-3xl px-4">
                <h1 className="text-xl font-bold text-gray-900 mb-2">Transactions</h1>
                <p className="text-sm text-gray-500 mb-6">
                    Look up transactions by user ID, username, or email. Leave blank to fetch all recent transactions.
                </p>

                {currentView === 'search' && (
                    <form onSubmit={handleSubmit}>
                        <label
                            htmlFor="userId"
                            className="block text-sm font-medium text-gray-900 mb-1"
                        >
                            User ID / Username / Email (optional)
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                name="userId"
                                id="userId"
                                defaultValue={userId}
                                placeholder="Leave blank for all recent transactions"
                                className="flex-1 block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                            <Button type="submit">Search</Button>
                        </div>
                    </form>
                )}

                {currentView === 'loader' && <Loader />}

                {currentView === 'error' && (
                    <AlertComponent
                        callBack={() => setCurrentView('search')}
                        type="error"
                        subject="Failed"
                        message={message}
                    />
                )}

                {currentView === 'results' && (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-sm text-gray-600">
                                {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
                                {userId ? ` for "${userId}"` : ' (all)'}
                            </p>
                            <BackButton callBack={() => setCurrentView('search')} />
                        </div>

                        {transactions.length === 0 ? (
                            <div className="text-center py-12 text-gray-500 text-sm">
                                No transactions found.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {transactions.map((tx) => (
                                    <div
                                        key={tx.id}
                                        className="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
                                    >
                                        <div className="flex items-start justify-between gap-2 flex-wrap">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${typeColor(tx.type)}`}>
                                                    {tx.type}
                                                </span>
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor(tx.status)}`}>
                                                    {tx.status}
                                                </span>
                                            </div>
                                            <span className="text-xs text-gray-400">{fmtDate(tx.created_at)}</span>
                                        </div>

                                        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                            <div>
                                                <span className="text-gray-500">Amount: </span>
                                                <span className="font-semibold text-gray-900">{fmtCurrency(tx.amount)}</span>
                                            </div>
                                            {tx.commission !== undefined && (
                                                <div>
                                                    <span className="text-gray-500">Commission: </span>
                                                    <span className="font-semibold text-gray-900">{fmtCurrency(tx.commission)}</span>
                                                </div>
                                            )}
                                            {tx.charged_fees !== undefined && (
                                                <div>
                                                    <span className="text-gray-500">Fees: </span>
                                                    <span className="text-gray-700">{fmtCurrency(tx.charged_fees)}</span>
                                                </div>
                                            )}
                                            {tx.event?.name && (
                                                <div className="col-span-2">
                                                    <span className="text-gray-500">Event: </span>
                                                    <span className="text-gray-700">{tx.event.name}</span>
                                                </div>
                                            )}
                                            {tx.user?.username && (
                                                <div>
                                                    <span className="text-gray-500">User: </span>
                                                    <span className="text-gray-700">@{tx.user.username}</span>
                                                </div>
                                            )}
                                            {tx.paypal_order_id && (
                                                <div className="col-span-2">
                                                    <span className="text-gray-500">PayPal order: </span>
                                                    <span className="text-xs text-gray-500 font-mono">{tx.paypal_order_id}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-2 text-[10px] text-gray-300 font-mono">
                                            {String(tx.id).slice(-12)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <Footer />
            <ApplicationBar />
        </div>
    );
};

export default Transactions;
